import { useCallback, useEffect, useState } from 'react';
import { useClinicalEditors, getErrorMessage } from './context';

import type {
  ProfileEditRequest,
  ProfileEditRequestPayload,
} from '@rudi/shared/types/clinical-trials';
export type {
  ClinicRefDto,
  ProfileEditRequest,
  ProfileEditRequestPayload,
} from '@rudi/shared/types/clinical-trials';
interface UploadedImage {
  objectKey: string;
  url: string;
}

const BASE = '/clinical-trials/profile-edit-request';

export function useProfileEditRequest(slug?: string | null, enabled = true) {
  const { api: apiClient } = useClinicalEditors();
  const [latest, setLatest] = useState<ProfileEditRequest | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    if (!enabled) {
      setLatest(null);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setLatest(null);
    try {
      const params = slug ? { slug } : undefined;
      const { data } = await apiClient.get<ProfileEditRequest | null>(`${BASE}/me/latest`, {
        params,
      });
      setLatest(data ?? null);
      setError(null);
    } catch (err) {
      setError(getErrorMessage(err));
      setLatest(null);
    } finally {
      setLoading(false);
    }
  }, [apiClient, enabled, slug]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const save = useCallback(
    async (payload: ProfileEditRequestPayload): Promise<ProfileEditRequest> => {
      setSaving(true);
      try {
        const { data } = await apiClient.post<ProfileEditRequest>(`${BASE}/me`, payload);
        setLatest(data);
        setError(null);
        return data;
      } catch (err) {
        const msg = getErrorMessage(err);
        setError(msg);
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [apiClient]
  );

  const uploadPhoto = useCallback(
    async (file: File): Promise<UploadedImage> => {
      const form = new FormData();
      form.append('file', file);
      const { data } = await apiClient.post<UploadedImage>(`${BASE}/me/photo`, form);
      return data;
    },
    [apiClient]
  );

  return { latest, loading, error, saving, save, refresh, uploadPhoto };
}
